import { useState } from "react";
import { AlertTriangle, Loader2, Lock } from "lucide-react";

export default function SignIn({ login, busy, error }) {
    const [token, setToken] = useState("");

    const submit = (e) => {
        e.preventDefault();
        if (token.trim()) login(token.trim());
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8">
                    <img
                        src="/edara-logo.png"
                        alt=""
                        className="w-12 h-12 object-contain"
                    />
                    <span className="font-display text-2xl tracking-wider">
                        EDARA <span className="text-[#1FA8FF]">SOFTWARE</span>
                    </span>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1FA8FF] mb-3">
                        <Lock className="w-3 h-3" />
                        Admin console
                    </div>
                    <h1 className="font-display text-3xl leading-none">
                        Sign in to operate.
                    </h1>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                        Enter the admin token configured in the backend
                        environment. The token is exchanged for a short-lived,
                        httpOnly session cookie — it is never stored in
                        browser storage.
                    </p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <label className="flex flex-col gap-1.5">
                            <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                                Admin token
                            </span>
                            <input
                                type="password"
                                value={token}
                                autoFocus
                                onChange={(e) => setToken(e.target.value)}
                                className="px-3.5 py-3 rounded-lg border border-white/10 bg-[#0A0A0A] text-white placeholder-zinc-600 focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none"
                                placeholder="Admin token"
                                data-testid="admin-token-input"
                            />
                        </label>

                        {error && (
                            <div
                                className="flex items-start gap-2 text-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-3"
                                role="alert"
                            >
                                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy || !token}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#1FA8FF] text-white font-semibold hover:bg-[#0C84D4] disabled:opacity-60 transition-colors"
                            data-testid="admin-signin"
                        >
                            {busy ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Authenticating…
                                </>
                            ) : (
                                <>Sign in</>
                            )}
                        </button>
                    </form>

                    <p className="mt-6 pt-6 border-t border-white/10 text-xs text-zinc-500 leading-relaxed">
                        Token is rotated by changing{" "}
                        <code className="text-zinc-300">ADMIN_TOKEN</code> in
                        the backend .env and restarting the service. Webhook
                        delivery is configured via{" "}
                        <code className="text-zinc-300">WEBHOOK_URL</code> and
                        optional{" "}
                        <code className="text-zinc-300">WEBHOOK_SECRET</code>{" "}
                        for HMAC-SHA256 signing.
                    </p>
                </div>

                <a
                    href="/"
                    className="block text-center mt-6 text-sm text-zinc-500 hover:text-white"
                >
                    ← Back to website
                </a>
            </div>
        </div>
    );
}
